import { z } from 'zod';

import { endpoint } from '@/services/api/endpoint';
import { CompanyPathSchema } from '@/services/api/schemas';

import {
  SyncErpIndexesResponseSchema,
  ErpConnectionsSchema,
  ZohoOrganizationsSchema,
  ZohoSaveOrganizationBodySchema,
  HashDevicesSchema,
  ErpCompaniesQuerySchema,
  ErpCompaniesSchema,
  ActivateErpQuerySchema,
  ErpSSOParamsQuerySchema,
  ErpSSOParamsSchema,
  FTPConnectionBodySchema,
  FTPConnectionSchema,
  FTPConnectionSaveResponseSchema,
} from '@/domain/erpIntegration/schemas';
import {
  ErpPreferenceFieldsResponseSchema,
  ErpPreferencesSaveBodySchema,
} from '@/domain/erpIntegration/preferenceFieldSchemas';
import {
  PriorityCustomerApiConnectionBodySchema,
  PriorityCustomerApiConnectionSaveResponseSchema,
} from '@/domain/erpIntegration/priorityCustomerApiConnectionSchemas';

export const erpIntegrationApi = {
  getConnections: endpoint({
    method: 'GET',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/connections`,
    path: CompanyPathSchema,
    response: ErpConnectionsSchema,
  }),

  getPreferenceFields: endpoint({
    method: 'GET',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/erp_config`,
    path: CompanyPathSchema,
    response: ErpPreferenceFieldsResponseSchema,
  }),

  savePreferences: endpoint({
    method: 'POST',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/erp_config`,
    path: CompanyPathSchema,
    body: ErpPreferencesSaveBodySchema,
    response: ErpPreferenceFieldsResponseSchema,
  }),

  importPreferences: endpoint({
    method: 'POST',
    url: 'importData',
    body: z.instanceof(FormData),
    response: ErpPreferenceFieldsResponseSchema,
  }),

  savePriorityCustomerApiConnection: endpoint({
    method: 'POST',
    url: 'priority_api/customer_authorization',
    body: PriorityCustomerApiConnectionBodySchema,
    response: PriorityCustomerApiConnectionSaveResponseSchema,
  }),

  syncErpIndexes: endpoint({
    method: 'POST',
    url: (p: { companyId: string }) => `company/${p.companyId}/sync_indexes`,
    path: CompanyPathSchema,
    response: SyncErpIndexesResponseSchema,
  }),

  getZohoOrganizations: endpoint({
    method: 'GET',
    url: 'zoho/organization',
    response: ZohoOrganizationsSchema,
  }),

  saveZohoOrganization: endpoint({
    method: 'POST',
    url: 'zoho/organization',
    body: ZohoSaveOrganizationBodySchema,
    response: z.unknown(),
  }),

  getHashDevices: endpoint({
    method: 'GET',
    url: 'getHashDevices',
    response: HashDevicesSchema,
  }),

  getCompaniesByErpType: endpoint({
    method: 'GET',
    url: 'getCompaniesByErpType',
    query: ErpCompaniesQuerySchema,
    response: ErpCompaniesSchema,
  }),

  getFTPConnection: endpoint({
    method: 'GET',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/ftp_connection`,
    path: CompanyPathSchema,
    response: FTPConnectionSchema,
  }),

  activate: endpoint({
    method: 'GET',
    url: 'activateErp',
    query: ActivateErpQuerySchema,
    response: z.unknown(),
  }),

  disconnect: endpoint({
    method: 'GET',
    url: 'disconnectErp',
    response: z.unknown(),
  }),

  getSSOParams: endpoint({
    method: 'GET',
    url: 'getSSOParams',
    query: ErpSSOParamsQuerySchema,
    response: ErpSSOParamsSchema,
  }),

  saveFTPConnection: endpoint({
    method: 'POST',
    url: (p: { companyId: string }) => `preferences/company/${p.companyId}/ftp_connection`,
    path: CompanyPathSchema,
    body: FTPConnectionBodySchema,
    response: FTPConnectionSaveResponseSchema,
  }),
};
